/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { useSpring, animated, config } from '@react-spring/web';
import { format } from 'date-fns';
import Fuse from 'fuse.js';
import { ArrowLeft, ChevronDown, Home, Repeat } from 'lucide-react';
import { createContext, forwardRef, useEffect, useState, useMemo, ReactNode, useRef } from 'react';
import { SetURLSearchParams, useNavigate, useParams, useSearchParams } from 'react-router';
import Split from 'react-split';
import { IoProvider } from 'socket.io-react-hook';

import { useRunViewerVideoPlayer } from './useRunViewerVideo';

import { IWithPagination, RunViewerData, SuiteExecutionTrigger } from '@/commons/commons';
import { RunExecution, RunExecutionDetails, RunStatus } from '@/commons/protocols';
import { formatError } from '@/components/atoms/Error';
import ExecutionCard from '@/components/executions/ExecutionCard';
import { ExecutionSelector } from '@/components/executions/ExecutionSelector';
import { ExecutionStatusBadge } from '@/components/executions/ExecutionStatusBadge';
import ReportDialog from '@/components/runViewer/ReportDialog';
import SuiteActions from '@/components/suites/SuiteActions';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { useMutate, useGetInfinite, useGet } from '@/net/commons';
import { cn, displayUser } from '@/utils';
import { getExecutionSummaryData } from '@/utils/executions';

type StepContextType = {
    runStatus: RunStatus;
    highlightedStepRank: number | undefined;
    highlightedAction: number | undefined;
    handleClickOnStepOrAction: (stepRank: number, actionRank?: number) => void;
};

export const StepContext = createContext<StepContextType>({
    runStatus: 'queued',
    highlightedStepRank: undefined,
    highlightedAction: undefined,
    handleClickOnStepOrAction: () => {},
});

const useThrottledEffect = (callback: () => void, deps: any[], delay: number) => {
    const lastRun = useRef(Date.now());

    useEffect(() => {
        const now = Date.now();
        if (now - lastRun.current >= delay) {
            callback();
            lastRun.current = now;
        } else {
            const timeoutId = setTimeout(
                () => {
                    callback();
                    lastRun.current = Date.now();
                },
                delay - (now - lastRun.current),
            );

            return () => clearTimeout(timeoutId);
        }
    }, deps);
};

const RunViewerVideoOnly = forwardRef<
    null,
    {
        projectSlug: string;
        suiteSlug: string;
        executionId: string;
        runId: string;
        onStepsComponentReady?: (component: ReactNode) => void;
        onCancelRunReady?: (cancelFn: () => void) => void;
        onAttemptsComponentReady?: (component: ReactNode | null) => void;
        onAttemptChange?: (attempt: number | null) => void;
    }
>(
    (
        {
            projectSlug,
            suiteSlug,
            executionId,
            runId,
            onStepsComponentReady,
            onCancelRunReady,
            onAttemptsComponentReady,
            onAttemptChange,
        },
        ref,
    ) => {
        const [status, setStatus] = useState<undefined | RunStatus | 'running-testagent'>(undefined);
        const [searchParams, setSearchParams] = useSearchParams();
        const [attempt, setAttempt] = useState<number | null>(null);

        const attemptParam = ((a) => {
            if (a === null) return undefined;
            const p = parseInt(a);
            if (isNaN(p)) return undefined;
            return p;
        })(searchParams.get('attempt'));

        const { data, isLoading } = useGet<RunViewerData>(
            `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}${attemptParam ? `?attempt=${attemptParam}` : ''}`,
            {
                refreshInterval: () =>
                    status === 'queued' || status === undefined || status === 'running-testagent' ? 500 : 0,
            },
        );

        const dataRunId = data?.run.id.toString() || '';

        useEffect(() => {
            if (data?.run.status === 'finished' || data?.run.status === 'failed') {
                const newAttempt = attemptParam ? attemptParam : data.runAttempt?.attempt || 1;
                setAttempt(newAttempt);
                onAttemptChange?.(newAttempt);
            }
        }, [dataRunId, attemptParam]);

        const { videoComponent, stepsComponent, cancelRun } = useRunViewerVideoPlayer({
            runData: data,
            projectSlug,
            suiteSlug,
            executionId,
            runId,
            videoPlayerClassName: '!max-h-[calc(100vh-23.5rem)]',
        });

        useThrottledEffect(
            () => {
                if (stepsComponent && onStepsComponentReady) {
                    onStepsComponentReady(stepsComponent);
                }
            },
            [data, stepsComponent, onStepsComponentReady],
            200,
        );

        useEffect(() => {
            if (onAttemptsComponentReady) {
                if (data?.attemptIndexes && data.attemptIndexes.length > 1) {
                    const attemptsComponent = (
                        <div className="flex items-center gap-2">
                            <Select
                                value={attempt?.toString()}
                                onValueChange={(value) => {
                                    const newAttempt = parseInt(value);
                                    onAttemptChange?.(newAttempt);
                                    setAttempt(newAttempt);
                                    setSearchParams((prev) => {
                                        prev.set('attempt', newAttempt.toString());
                                        return prev;
                                    });
                                }}
                            >
                                <SelectTrigger className="h-10 px-4 py-2 gap-x-2 text-xs bg-white">
                                    <SelectValue placeholder={`Attempt ${attempt}`} />
                                </SelectTrigger>
                                <SelectContent>
                                    {data.attemptIndexes.map((a) => (
                                        <SelectItem key={a} value={a.toString()} className="text-xs">
                                            Attempt {a}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    );
                    onAttemptsComponentReady(attemptsComponent);
                } else {
                    onAttemptsComponentReady(null);
                }
            }
        }, [data, attempt, onAttemptsComponentReady]);

        useEffect(() => {
            if (data?.run?.status !== status) {
                setStatus(data?.run?.status);
            }
        }, [data, isLoading, status]);

        useEffect(() => {
            if (cancelRun && onCancelRunReady) {
                onCancelRunReady(cancelRun);
            }
        }, [cancelRun, onCancelRunReady]);

        if (isLoading) {
            return <div>Loading...</div>;
        } else if (!data) {
            return <div>No data</div>;
        }

        return (
            <div className="flex flex-col h-full w-full">
                <div className="flex-grow overflow-hidden">{videoComponent}</div>
            </div>
        );
    },
);

// The IoProvider wrapper
const RunViewerVideoOnlyWithIoProvider = ({
    projectSlug,
    suiteSlug,
    executionId,
    runId,
    onStepsComponentReady,
    onCancelRunReady,
    onAttemptsComponentReady,
    onAttemptChange,
}: {
    projectSlug: string;
    suiteSlug: string;
    executionId: string;
    runId: string;
    onStepsComponentReady?: (component: ReactNode) => void;
    onCancelRunReady?: (cancelFn: () => void) => void;
    onAttemptsComponentReady?: (component: ReactNode | null) => void;
    onAttemptChange?: (attempt: number | null) => void;
}) => {
    return (
        <IoProvider>
            <RunViewerVideoOnly
                projectSlug={projectSlug}
                suiteSlug={suiteSlug}
                executionId={executionId}
                runId={runId}
                onStepsComponentReady={onStepsComponentReady}
                onCancelRunReady={onCancelRunReady}
                onAttemptsComponentReady={onAttemptsComponentReady}
                onAttemptChange={onAttemptChange}
            />
        </IoProvider>
    );
};

// Custom hook for managing filter state and URL parameters
const useFilterParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const searchQuery = searchParams.get('search') || '';
    const statusFilter = searchParams.get('status') || 'ALL';

    const setSearchQuery = (value: string) => {
        setSearchParams((prev) => {
            if (value) {
                prev.set('search', value);
            } else {
                prev.delete('search');
            }
            return prev;
        });
    };

    const setStatusFilter = (value: string) => {
        setSearchParams((prev) => {
            if (value !== 'ALL') {
                prev.set('status', value);
            } else {
                prev.delete('status');
            }
            return prev;
        });
    };

    const resetFilters = () => {
        setSearchParams((prev) => {
            prev.delete('search');
            prev.delete('status');
            return prev;
        });
    };

    return {
        searchQuery,
        statusFilter,
        setSearchQuery,
        setStatusFilter,
        resetFilters,
        searchParams,
        setSearchParams,
    };
};

// Custom hook for managing execution data and operations
export const useExecutionData = (projectSlug?: string, suiteSlug?: string) => {
    const { searchParams, setSearchParams } = useFilterParams();
    const pageSize = searchParams.get('pageSize') || 20;
    const executionId = searchParams.get('executionId');
    const [focusedExecutionId, setFocusedExecutionId] = useState<string | undefined>(executionId || undefined);

    const getKey = (pageIndex: number, previousPageData: IWithPagination<RunExecutionDetails> | null) => {
        if (previousPageData && !previousPageData.data.length) return null;
        return `/bff/project/${projectSlug}/suite/${suiteSlug}/executions?page=${pageIndex + 1}&pageSize=${pageSize}`;
    };

    const {
        data: executionsPages,
        isLoading: isExecutionsLoading,
        error: executionsError,
        mutate,
        size,
        setSize,
    } = useGetInfinite<IWithPagination<RunExecutionDetails>>(getKey);

    const executions = useMemo(() => {
        if (!executionsPages) return null;
        return {
            data: executionsPages.flatMap((page) => page.data),
            pagination: executionsPages[executionsPages.length - 1]?.pagination,
        };
    }, [executionsPages]);

    const updateFocusedExecution = (id: string) => {
        setFocusedExecutionId(id);
        setSearchParams((prev) => {
            prev.set('executionId', id);
            return prev;
        });
    };

    useEffect(() => {
        if (!executions) return;
        if (executions.data.length === 0) {
            return;
        }
        if (Number(focusedExecutionId) > executions.data[0].id) {
            return;
        } else if (
            (!focusedExecutionId && executions.data.length > 0) ||
            !executions.data.some((execution) => execution.id.toString() === focusedExecutionId)
        ) {
            updateFocusedExecution(executions.data[0].id.toString());
        }
    }, [executions, focusedExecutionId]);

    useEffect(() => {
        setFocusedExecutionId(executionId ?? undefined);
    }, [executionId]);
    const currentExecution = executions?.data.find((e) => e.id.toString() === focusedExecutionId);
    const executionSummary = currentExecution ? getExecutionSummaryData(currentExecution) : null;

    return {
        executions,
        isExecutionsLoading,
        executionsError,
        mutate,
        size,
        setSize,
        focusedExecutionId,
        updateFocusedExecution,
        currentExecution,
        executionSummary,
    };
};

// Component for execution header with navigation and selection
const ExecutionHeader = ({
    currentExecution,
    isExecutionSelectorOpen,
    setIsExecutionSelectorOpen,
    executions,
    focusedExecutionId,
    updateFocusedExecution,
    triggerReload,
    size,
    setSize,
    executionSummary,
    statusFilter,
    setStatusFilter,
    currentRun,
    selectedRuns,
    handleRun,
    handleToggleEnable,
    setSearchParams,
    handleArchive,
    attemptsComponentNode,
    currentAttempt,
}: {
    currentExecution: RunExecutionDetails | undefined;
    isExecutionSelectorOpen: boolean;
    setIsExecutionSelectorOpen: (value: boolean) => void;
    executions: { data: RunExecutionDetails[]; pagination?: any } | null;
    focusedExecutionId: string | undefined;
    updateFocusedExecution: (id: string) => void;
    triggerReload: () => void;
    size: number;
    setSize: (size: number) => void;
    executionSummary: ReturnType<typeof getExecutionSummaryData> | null;
    statusFilter: string;
    setStatusFilter: (value: string) => void;
    currentRun: RunExecution | undefined;
    selectedRuns: { runId: number; executionId: number; storyId?: number }[];
    handleRun: (storyIds: number[]) => Promise<void>;
    handleToggleEnable: (storyId: number, enabled: boolean) => Promise<void>;
    setSearchParams: SetURLSearchParams;
    handleArchive: (storyId: number) => Promise<void>;
    attemptsComponentNode: ReactNode | null;
    currentAttempt: number | null;
}) => {
    const user = displayUser(currentExecution?.triggerUser, true);
    const isUserCI = user === 'CI';
    return (
        <div className="flex flex-wrap items-center gap-x-[24px] min-h-[50px]">
            <div className="flex items-center gap-x-[10px] text-slate-700 font-[400] text-[18px] py-[8px]">
                <a href="/">
                    <Home className="text-black" />
                </a>
                <span className="">
                    <span className="text-gray-400">/&nbsp;</span>
                    <span>Runs</span>
                    <span className="text-gray-400">&nbsp;/</span>
                </span>
                <Popover open={isExecutionSelectorOpen} onOpenChange={setIsExecutionSelectorOpen}>
                    <PopoverTrigger asChild>
                        <button className="flex items-center">
                            Run on{' '}
                            {currentExecution
                                ? format(new Date(currentExecution.createdAt), 'MMM dd, yyyy HH:mm')
                                : '...'}
                            <ChevronDown className="inline-flex" />
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="start"
                        className="w-[820px] max-w-[90vw] h-full overflow-auto max-h-[80vh] p-[16px]"
                    >
                        {executions && (
                            <ExecutionSelector
                                executions={executions.data}
                                onSelect={(id) => {
                                    updateFocusedExecution(id);
                                    setIsExecutionSelectorOpen(false);
                                    setSearchParams((prev) => {
                                        prev.set('executionId', id);
                                        prev.delete('stepViewer');
                                        return prev;
                                    });
                                }}
                                currentExecutionId={focusedExecutionId}
                                pagination={executions.pagination}
                                onLoadMore={() => setSize(size + 1)}
                            />
                        )}
                    </PopoverContent>
                </Popover>
            </div>
            <div className="flex gap-x-[24px] py-2 items-center">
                {executionSummary && (
                    <>
                        {Object.keys(executionSummary).map((key) => {
                            const enforceKey = key as keyof typeof executionSummary;
                            return (
                                <ExecutionStatusBadge
                                    key={enforceKey}
                                    status={enforceKey.toUpperCase() as 'PASS' | 'FAIL' | 'OTHER'}
                                    number={executionSummary[enforceKey]}
                                    onClick={() => {
                                        if (statusFilter === enforceKey.toUpperCase()) {
                                            setStatusFilter('ALL');
                                        } else {
                                            setStatusFilter(enforceKey.toUpperCase());
                                        }
                                    }}
                                />
                            );
                        })}
                    </>
                )}
            </div>
            <div className="ml-auto flex gap-x-[24px] py-2 items-center">
                {currentExecution?.triggerUser && (
                    <span className="text-large font-normal text-gray-500 text-gray-800">
                        <span className="text-gray-500 text-body-medium">Run by </span>
                        <span
                            className={cn(
                                isUserCI
                                    ? 'border-gray-200 bg-slate-300 w-[28px] h-[28px] rounded-[3px] inline-flex items-center justify-center text-gray-600 text-body font-[700]'
                                    : '',
                            )}
                        >
                            {user}
                        </span>
                    </span>
                )}
                <RunActions
                    currentRun={currentRun}
                    selectedRuns={selectedRuns}
                    handleRun={handleRun}
                    handleToggleEnable={handleToggleEnable}
                    triggerReload={triggerReload}
                    handleArchive={handleArchive}
                    attemptsComponentNode={attemptsComponentNode}
                    currentAttempt={currentAttempt}
                />
            </div>
        </div>
    );
};

// Component for run action buttons
const RunActions = ({
    currentRun,
    selectedRuns,
    handleRun,
    handleToggleEnable,
    triggerReload,
    handleArchive,
    attemptsComponentNode,
    currentAttempt,
}: {
    currentRun: RunExecution | undefined;
    selectedRuns: { runId: number; executionId: number; storyId?: number }[];
    handleRun: (storyIds: number[]) => Promise<void>;
    handleToggleEnable: (storyId: number, enabled: boolean) => Promise<void>;
    triggerReload: () => void;
    handleArchive: (storyId: number) => Promise<void>;
    attemptsComponentNode: ReactNode | null;
    currentAttempt: number | null;
}) => {
    const { projectSlug, suiteSlug } = useParams();
    const [searchParams] = useSearchParams();
    const runSearchParams = searchParams.get('run');
    const runId = runSearchParams ? runSearchParams.split('_')[0] : undefined;
    const executionId = searchParams.get('executionId') || undefined;

    const multipleSelected = selectedRuns.length > 1;

    if ((!currentRun && !multipleSelected) || !projectSlug || !suiteSlug) return null;

    return (
        <div className="flex gap-2 ml-auto">
            {!multipleSelected && attemptsComponentNode}
            <Button
                className="gap-x-2 whitespace-nowrap"
                variant="outline"
                size="default"
                onClick={() => {
                    if (multipleSelected) {
                        const storyIds = selectedRuns
                            .map((run) => run.storyId)
                            .filter((id): id is number => id !== undefined);
                        handleRun(storyIds);
                    } else if (currentRun) {
                        handleRun([currentRun.storyId]);
                    }
                }}
            >
                <Repeat size="16" />
                {multipleSelected ? `Rerun All (${selectedRuns.length})` : 'Rerun'}
            </Button>
            {multipleSelected ? (
                <ReportDialog
                    handleSuccess={triggerReload}
                    projectSlug={projectSlug}
                    suiteSlug={suiteSlug}
                    selectedRuns={selectedRuns}
                    executionId={executionId}
                />
            ) : (
                <SingleRunReportDialog handleSuccess={triggerReload} projectSlug={projectSlug} suiteSlug={suiteSlug} />
            )}
            {!multipleSelected && currentRun && (
                <SuiteActions
                    triggerComponent={
                        <Button variant="outline" className="gap-x-2 whitespace-nowrap" size="default">
                            Actions
                            <ChevronDown size="16" className="shrink-0" />
                        </Button>
                    }
                    handleArchive={() => handleArchive(currentRun.storyId)}
                    projectSlug={projectSlug}
                    suiteSlug={suiteSlug}
                    storyName={currentRun.storyName}
                    storySlug={currentRun.storySlug}
                    storyId={currentRun.storyId}
                    draft={currentRun.storyDraft}
                    submiting={false}
                    archived={currentRun.storyArchived}
                    maintenance={currentRun.storyMaintenance}
                    handleToggleEnable={() =>
                        handleToggleEnable(
                            currentRun.storyId,
                            currentRun.storyEnabled !== undefined ? !currentRun.storyEnabled : false,
                        )
                    }
                    enabled={currentRun.storyEnabled}
                    executionId={executionId}
                    runId={runId}
                    isFinished={currentRun.status === 'finished'}
                    attempt={currentAttempt}
                />
            )}
        </div>
    );
};

const SingleRunReportDialog = ({
    projectSlug,
    suiteSlug,
    handleSuccess,
}: {
    projectSlug: string;
    suiteSlug: string;
    handleSuccess?: () => void;
}) => {
    const [searchParams] = useSearchParams();
    const runSearchParams = searchParams.get('run');
    const [runId, executionId] = runSearchParams ? runSearchParams.split('_') : [undefined, undefined];
    const { data, isLoading } = useGet<RunViewerData>(
        runId && executionId
            ? `/bff/project/${projectSlug}/suite/${suiteSlug}/execution/${executionId}/run/${runId}`
            : null,
    );

    if (isLoading || !data) {
        return null;
    }

    return (
        <ReportDialog
            currentOutput={data.result?.output}
            currentReport={data.result?.report}
            projectSlug={projectSlug || ''}
            suiteSlug={suiteSlug || ''}
            runId={runId || ''}
            executionId={executionId || ''}
            handleSuccess={handleSuccess}
        />
    );
};

// Component for filter controls
const FilterControls = ({
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
}: {
    statusFilter: string;
    setStatusFilter: (value: string) => void;
    searchQuery: string;
    setSearchQuery: (value: string) => void;
}) => (
    <div className="grid grid-cols-[min-content_auto] gap-4 me-[16px] pb-[8px] pt-[7px]">
        <div className="flex flex-col gap-2 w-[78px]">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="border-0 p-0 hover:bg-transparent focus:ring-0 text-black font-bold">
                    <div className="flex items-center whitespace-nowrap gap-2 mr-1">
                        <SelectValue placeholder="Filter by status" />
                    </div>
                </SelectTrigger>
                <SelectContent>
                    <SelectItem value="ALL">All Results</SelectItem>
                    <SelectItem value="PASS">Passed</SelectItem>
                    <SelectItem value="FAIL">Failed</SelectItem>
                    <SelectItem value="PENDING">In Review</SelectItem>
                    <SelectItem value="MAINTENANCE">In Repair</SelectItem>
                    <SelectItem value="DISABLED">Disabled</SelectItem>
                    <SelectItem value="DISMISSED">Dismissed</SelectItem>
                    <SelectItem value="DRAFT">In Draft</SelectItem>
                    <SelectItem value="RUNNING">Running</SelectItem>
                    <SelectItem value="OTHER">Other</SelectItem>
                </SelectContent>
            </Select>
        </div>
        <div className="flex flex-col gap-2">
            <Input
                placeholder="Search by story title..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className=""
            />
        </div>
    </div>
);

const useFilteredRuns = (
    currentExecution: RunExecutionDetails | undefined,
    searchQuery: string,
    statusFilter: string,
) => {
    // Memoize Fuse instance to avoid recreating it on every render
    const fuse = useMemo(() => {
        if (!currentExecution) return null;
        return new Fuse(currentExecution.runs, {
            keys: ['storyName', 'feedback'],
            threshold: 0.3,
            /* @ts-ignore this option actually exists */
            ignoreDiacritics: true,
        });
    }, [currentExecution]);

    return useMemo(() => {
        if (!currentExecution) return { filtered: [], hiddenCount: 0 };

        const allRuns = [...currentExecution.runs];
        let filtered = allRuns;

        // Status filter
        if (statusFilter !== 'ALL') {
            filtered = filtered.filter((run) => {
                // Move filter logic to a separate function to reduce complexity
                return filterRunByStatus(run, statusFilter);
            });
        }

        // Search filter - only run if we have a query
        if (searchQuery && fuse) {
            filtered = fuse.search(searchQuery).map((result) => result.item);
        }

        return {
            filtered,
            hiddenCount: allRuns.length - filtered.length,
        };
    }, [currentExecution, searchQuery, statusFilter, fuse]);
};

// Extract filter logic to reduce complexity
const filterRunByStatus = (run: any, status: string) => {
    switch (status) {
        case 'PASS':
            return run.output === 'PASS';
        case 'FAIL':
            return run.output === 'FAIL';
        case 'OTHER':
            return (
                (run.output !== 'PASS' && run.output !== 'FAIL') ||
                run.storyMaintenance ||
                run.storyDraft ||
                !run.storyEnabled
            );
        case 'PENDING':
            return run.output === 'PENDING';
        case 'MAINTENANCE':
            return run.output === 'DISMISSED' && run.storyMaintenance;
        case 'DISMISSED':
            return run.output === 'DISMISSED' && !run.storyMaintenance;
        case 'DISABLED':
            return !run.storyEnabled;
        case 'DRAFT':
            return run.storyDraft;
        case 'RUNNING':
            return !run.output && (run.status === 'running' || run.status === 'queued');
        default:
            return true;
    }
};

const Executions = () => {
    const { projectSlug, suiteSlug } = useParams();
    const [submitError, setSubmitError] = useState<any>(undefined);
    const [isExecutionSelectorOpen, setIsExecutionSelectorOpen] = useState(false);
    const [stepsComponent, setStepsComponent] = useState<ReactNode>(null);
    const [socketCancelRun, setSocketCancelRun] = useState<(() => void) | undefined>(undefined);
    const [attemptsComponentNode, setAttemptsComponentNode] = useState<ReactNode | null>(null);
    const [currentAttempt, setCurrentAttempt] = useState<number | null>(null);

    const { searchQuery, statusFilter, setSearchQuery, setStatusFilter, resetFilters, searchParams, setSearchParams } =
        useFilterParams();

    const stepViewer = searchParams.get('stepViewer');

    const setStepViewer = (value: boolean) => {
        setSearchParams((prev) => {
            if (value) {
                prev.set('stepViewer', value.toString());
            } else {
                prev.delete('stepViewer');
            }
            return prev;
        });
    };

    const {
        executions,
        isExecutionsLoading,
        executionsError,
        mutate,
        size,
        setSize,
        focusedExecutionId,
        updateFocusedExecution,
        currentExecution,
        executionSummary,
    } = useExecutionData(projectSlug, suiteSlug);

    const { trigger: trigerArchive } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/story/:id/archive`, {
        revalidate: true,
        method: 'PATCH',
    });

    const { trigger: triggerRun } = useMutate<SuiteExecutionTrigger>(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/trigger`,
        {
            revalidate: true,
            method: 'POST',
        },
    );

    const { trigger: triggerEditMeta } = useMutate(`/bff/project/${projectSlug}/suite/${suiteSlug}/story/:id`, {
        revalidate: true,
        method: 'PATCH',
    });

    const navigate = useNavigate();

    const handleToggleEnable = async (storyId: number, enabled: boolean) => {
        try {
            await triggerEditMeta({ idParam: storyId, enabled });
            mutate();
            toast({
                title: enabled ? 'Turned story on' : 'Turned story off',
            });
        } catch (err) {
            toast({
                title: `Error ${enabled ? 'disabling' : 'enabling'} story`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const handleArchive = async (storyId: number) => {
        try {
            await trigerArchive({ idParam: storyId });
            toast({
                title: `Archived story ${storyId}`,
            });
            mutate();
        } catch (err) {
            toast({
                title: `Error archiving story`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const redirectToExecution = (executionId: number) => {
        setSelectedRuns([]);
        setStepViewer(false);
        setSearchParams((prev) => {
            prev.set('executionId', executionId.toString());
            prev.delete('run');
            prev.delete('stepViewer');
            return prev;
        });
        navigate(`/suite/${projectSlug}/${suiteSlug}/executions?executionId=${executionId}`);
    };

    const redirectToRun = (executionId: number, runId: number, storyId: number) => {
        setSelectedRuns([
            {
                runId,
                executionId,
                storyId,
            },
        ]);
        setSearchParams((prev) => {
            prev.set('executionId', executionId.toString());
            prev.set('run', `${runId}_${executionId}`);
            return prev;
        });
        navigate(
            `/suite/${projectSlug}/${suiteSlug}/executions?executionId=${executionId}&run=${runId}_${executionId}&stepViewer=true`,
        );
    };

    const handleRun = async (storyIds: number[]) => {
        try {
            setSubmitError(undefined);
            const res = await triggerRun({ storyIds });
            toast({
                title: 'Run created',
                description: `Running ${storyIds.length} ${storyIds.length === 1 ? 'story' : 'stories'}.`,
            });
            mutate();
            if (storyIds.length === 1) {
                redirectToRun(Number(res?.execution.id), Number(res?.runMessages[0]?.runId), Number(storyIds[0]));
                return;
            } else if (storyIds.length > 1) {
                redirectToExecution(Number(res?.execution.id));
                return;
            }
        } catch (err) {
            setSubmitError(err);
            toast({
                title: `Error starting run`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    const initialRun = searchParams.get('run');
    // run param is runId_executionId

    const [selectedRuns, setSelectedRuns] = useState<{ runId: number; executionId: number; storyId?: number }[]>(
        initialRun
            ? [
                  {
                      runId: parseInt(initialRun.split('_')[0]),
                      executionId: parseInt(initialRun.split('_')[1]),
                  },
              ]
            : [],
    );

    useEffect(() => {
        setSearchParams((prev) => {
            prev.delete('attempt');
            return prev;
        });
    }, [selectedRuns]);

    // Add an event listener for the Escape key
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (stepViewer) {
                    setStepViewer(false);
                } else {
                    setSelectedRuns([]);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [stepViewer]);

    useEffect(() => {
        // Only update URL when a single run is selected
        if (selectedRuns.length === 1) {
            setSearchParams((prev) => {
                prev.set('run', `${selectedRuns[0].runId}_${selectedRuns[0].executionId}`);
                return prev;
            });
        } else {
            setSearchParams((prev) => {
                prev.delete('run');
                return prev;
            });
        }
    }, [selectedRuns, setSearchParams]);

    const handleSelectRun = (run: { runId: number; executionId: number; storyId?: number }, shiftKey: boolean) => {
        if (shiftKey) {
            // Check if run is already selected
            const isAlreadySelected = selectedRuns.some(
                (selectedRun) => selectedRun.runId === run.runId && selectedRun.executionId === run.executionId,
            );

            if (isAlreadySelected) {
                // If already selected, remove it
                setSelectedRuns(
                    selectedRuns.filter(
                        (selectedRun) =>
                            !(selectedRun.runId === run.runId && selectedRun.executionId === run.executionId),
                    ),
                );
            } else {
                // If not selected, add it
                setSelectedRuns([...selectedRuns, run]);
            }
        } else {
            // Just replace the selection
            setSelectedRuns([run]);
        }
    };

    // Replace the filteredRuns useMemo with the custom hook
    const filteredRuns = useFilteredRuns(currentExecution, searchQuery, statusFilter);

    const currentRun =
        selectedRuns.length === 1 ? currentExecution?.runs.find((run) => run.id === selectedRuns[0].runId) : undefined;

    // Add spring animation for panel transition
    const leftPanelSpring = useSpring({
        transform: stepViewer ? 'translateX(-100%)' : 'translateX(0%)',
        opacity: stepViewer ? 0 : 1,
        config: config.default,
    });

    const stepsComponentSpring = useSpring({
        transform: stepViewer ? 'translateX(0%)' : 'translateX(100%)',
        opacity: stepViewer ? 1 : 0,
        config: config.default,
    });

    if (executions?.data.length === 0) {
        return (
            <div className="flex flex-col h-full overflow-hidden rounded-[10px] bg-white mx-[6px] drop-shadow-[0_0_10px_rgba(0,0,0,0.05)] px-4 pt-2">
                <div className="grid w-full place-items-center h-full text-slate-500 text-h4">
                    No executions exist for this suite. Create your first story to start running tests.
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full overflow-hidden rounded-[10px] bg-white mx-[6px] drop-shadow-[0_0_10px_rgba(0,0,0,0.05)] px-4 pt-2">
            <div className="gap-[8px] text-sm text-slate-600 mb-4">
                <ExecutionHeader
                    currentExecution={currentExecution}
                    isExecutionSelectorOpen={isExecutionSelectorOpen}
                    setIsExecutionSelectorOpen={setIsExecutionSelectorOpen}
                    executions={executions}
                    focusedExecutionId={focusedExecutionId}
                    updateFocusedExecution={updateFocusedExecution}
                    size={size}
                    triggerReload={mutate}
                    setSize={setSize}
                    executionSummary={executionSummary}
                    statusFilter={statusFilter}
                    setStatusFilter={setStatusFilter}
                    currentRun={currentRun}
                    selectedRuns={selectedRuns}
                    handleRun={handleRun}
                    handleToggleEnable={handleToggleEnable}
                    setSearchParams={setSearchParams}
                    handleArchive={handleArchive}
                    attemptsComponentNode={attemptsComponentNode}
                    currentAttempt={currentAttempt}
                />
            </div>
            <Split
                className="flex items-start h-full overflow-hidden"
                gutterStyle={(_dimension, gutterSize) => {
                    return { width: `${gutterSize}px`, height: '100%', opacity: '0' };
                }}
                direction="horizontal"
                sizes={[30, 70]}
                gutterSize={16}
            >
                <div className="h-full gap-x-4 min-h-0 shrink-0 relative" style={{ width: '480px' }}>
                    <animated.div style={leftPanelSpring} className="absolute inset-0">
                        {isExecutionsLoading && <div>Loading...</div>}
                        {executionsError && <div>Error loading executions</div>}
                        <div className="w-full h-full flex flex-col">
                            {currentExecution && (
                                <>
                                    <FilterControls
                                        statusFilter={statusFilter}
                                        setStatusFilter={setStatusFilter}
                                        searchQuery={searchQuery}
                                        setSearchQuery={setSearchQuery}
                                    />
                                    <div className="overflow-y-auto">
                                        {filteredRuns.filtered.length > 0 && (
                                            <ExecutionCard
                                                key={currentExecution.id}
                                                execution={{
                                                    ...currentExecution,
                                                    runs: filteredRuns.filtered,
                                                }}
                                                displayLoading={false}
                                                projectSlug={projectSlug}
                                                suiteSlug={suiteSlug}
                                                defaultOpen
                                                socketCancelRun={socketCancelRun}
                                                handleArchive={handleArchive}
                                                handleRun={(storyId) => handleRun([storyId])}
                                                selectedRuns={selectedRuns}
                                                onSelectRun={(run, event) =>
                                                    handleSelectRun(
                                                        {
                                                            runId: run.id,
                                                            executionId: currentExecution.id,
                                                            storyId: run.storyId,
                                                        },
                                                        event.shiftKey,
                                                    )
                                                }
                                                openStepViewer={() => setStepViewer(true)}
                                            />
                                        )}
                                        {filteredRuns.hiddenCount > 0 && (
                                            <div className="text-body py-[8px] text-black mt-4 bg-slate-100 text-center">
                                                {filteredRuns.hiddenCount} story{' '}
                                                {filteredRuns.hiddenCount === 1 ? 'run' : 'runs'} hidden due to filters.{' '}
                                                <button
                                                    onClick={resetFilters}
                                                    className="text-blue-600 hover:underline"
                                                >
                                                    View all
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    </animated.div>
                    <animated.div style={stepsComponentSpring} className="absolute inset-0">
                        {stepsComponent && stepViewer && (
                            <div className="flex flex-col h-full">
                                <button
                                    onClick={() => setStepViewer(false)}
                                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-2"
                                >
                                    <ArrowLeft size={16} />
                                    <span>Back to runs</span>
                                </button>
                                {stepsComponent}
                            </div>
                        )}
                    </animated.div>
                </div>
                <div className="min-w-0 h-full w-full flex flex-col">
                    {selectedRuns.length === 1 && projectSlug && suiteSlug ? (
                        <RunViewerVideoOnlyWithIoProvider
                            projectSlug={projectSlug}
                            suiteSlug={suiteSlug}
                            executionId={selectedRuns[0].executionId.toString()}
                            runId={selectedRuns[0].runId.toString()}
                            onStepsComponentReady={setStepsComponent}
                            onCancelRunReady={(cancelFn) => {
                                if (typeof cancelFn === 'function') {
                                    setSocketCancelRun(() => cancelFn);
                                }
                            }}
                            onAttemptsComponentReady={setAttemptsComponentNode}
                            onAttemptChange={setCurrentAttempt}
                        />
                    ) : selectedRuns.length > 1 ? (
                        <div className="grid w-full place-items-center h-full text-slate-500 text-h4">
                            {selectedRuns.length} runs selected. Press ESC to clear selection.
                        </div>
                    ) : (
                        <div className="grid w-full place-items-center h-full text-slate-500 text-h4">
                            Select a run on the left to preview it
                        </div>
                    )}
                </div>
            </Split>
        </div>
    );
};

export default Executions;
